import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {number} */
  #requiredSizeOptions = 0;

  /** @type {Set<string>} */
  #confirmedSizeOptions = new Set();

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    // Listen for both click and change.
    // This means clicking Shopify's already-selected Small still counts.
    target?.addEventListener('click', this.#handleSizeInteraction, { signal });
    target?.addEventListener('change', this.#handleSizeInteraction, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    requestAnimationFrame(() => {
      this.#requiredSizeOptions = this.#getSizeOptionGroups().length;
      this.#updateButtonText();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          const rect = buyButtonsBlock.getBoundingClientRect();

          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * Handles the sticky add-to-cart button click.
   */
  handleAddToCartClick = async () => {
    if (this.#isAwaitingSizeConfirmation()) {
      this.#scrollToNextSizeOption();
      return;
    }

    if (!this.#targetAddToCartButton) return;

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([
      this.refs.addToCartButton,
      flyToCartElement,
    ]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Records when the customer deliberately clicks a size.
   * Clicking the already-selected default size also counts.
   *
   * @param {Event} event
   */
  #handleSizeInteraction = (event) => {
    const eventTarget = event.target;
    if (!(eventTarget instanceof Element)) return;

    // Opening a dropdown does not confirm the size.
    // Changing the dropdown does.
    const select = eventTarget.closest('select');

    if (select && event.type !== 'change') {
      return;
    }

    const optionGroup = eventTarget.closest('.variant-option');

    if (!(optionGroup instanceof HTMLElement)) return;
    if (!this.#isSizeOptionGroup(optionGroup)) return;

    this.#confirmedSizeOptions.add(
      this.#getSizeOptionKey(optionGroup)
    );

    this.#updateButtonText();
  };

  /**
   * Handles variant update events.
   *
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    const newStickyAddToCart =
      event.detail.data.html.querySelector('sticky-add-to-cart');

    if (!newStickyAddToCart) return;

    const newStickyBar =
      newStickyAddToCart.querySelector('[ref="stickyBar"]');

    if (!newStickyBar) return;

    const currentStuck =
      this.refs.stickyBar.getAttribute('data-stuck') || 'false';

    const variantAvailable =
      newStickyAddToCart.dataset.variantAvailable;

    morph(this.refs.stickyBar, newStickyBar, {
      childrenOnly: true,
    });

    this.refs.stickyBar.setAttribute(
      'data-stuck',
      currentStuck
    );

    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.#getProductForm();

    if (productForm) {
      this.#targetAddToCartButton =
        productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    this.#requiredSizeOptions =
      this.#getSizeOptionGroups().length;

    this.#updateButtonText();
  };

  /**
   * Handles variant selected events.
   *
   * @param {CustomEvent} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the selected variant title when a combination is unavailable.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector('.sticky-add-to-cart__variant');

    const productId = this.dataset.productId;

    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );

    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(
      variantPicker.querySelectorAll('input:checked')
    )
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Resets the puppet flag after cart success or error.
   *
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles product quantity updates.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Gets the product form.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId =
      sectionElement.id.replace('shopify-section-', '');

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the main variant picker.
   *
   * @returns {HTMLElement | null}
   */
  #getVariantPicker() {
    const productId = this.dataset.productId;
    const sectionElement = this.closest('.shopify-section');

    if (!productId || !sectionElement) return null;

    return sectionElement.querySelector(
      `variant-picker[data-product-id="${productId}"][ref="mainVariantPicker"]`
    );
  }

  /**
   * Gets the name of an option group.
   *
   * @param {HTMLElement} optionGroup
   * @returns {string}
   */
  #getOptionName(optionGroup) {
    const legend =
      optionGroup.querySelector(':scope > legend');

    const label =
      optionGroup.querySelector(':scope > label');

    return (
      legend?.textContent ||
      label?.textContent ||
      ''
    ).trim();
  }

  /**
   * Checks whether an option group is a selectable size option.
   *
   * @param {HTMLElement} optionGroup
   * @returns {boolean}
   */
  #isSizeOptionGroup(optionGroup) {
    const radios = Array.from(
      optionGroup.querySelectorAll('input[type="radio"]')
    );

    const select =
      optionGroup.querySelector('select');

    const valueCount =
      radios.length || select?.options.length || 0;

    // No customer choice is needed if there is only one value.
    if (valueCount <= 1) return false;

    const optionName =
      this.#getOptionName(optionGroup);

    if (/\bsize\b/i.test(optionName)) {
      return true;
    }

    // Fallback for option names such as Hoodie or Pants
    // where all values are clothing sizes.
    const values = radios.length
      ? radios.map((input) => input.value.trim())
      : Array.from(select?.options || []).map(
          (option) => option.value.trim()
        );

    const sizePattern =
      /^(XXS|XS|S|M|L|XL|XXL|XXXL|2XS|2XL|3XL|4XL|5XL|6XL|\d{1,3})$/i;

    return (
      values.length > 1 &&
      values.every((value) => sizePattern.test(value))
    );
  }

  /**
   * Creates a stable identifier for each size selector.
   *
   * @param {HTMLElement} optionGroup
   * @returns {string}
   */
  #getSizeOptionKey(optionGroup) {
    if (optionGroup.dataset.fieldsetIndex !== undefined) {
      return `fieldset-${optionGroup.dataset.fieldsetIndex}`;
    }

    return `option-${this.#getOptionName(optionGroup).toLowerCase()}`;
  }

  /**
   * Returns all selectable size groups.
   *
   * @returns {HTMLElement[]}
   */
  #getSizeOptionGroups() {
    const variantPicker = this.#getVariantPicker();
    if (!variantPicker) return [];

    return Array.from(
      variantPicker.querySelectorAll('.variant-option')
    ).filter(
      (optionGroup) =>
        optionGroup instanceof HTMLElement &&
        this.#isSizeOptionGroup(optionGroup)
    );
  }

  /**
   * Checks whether the customer still needs to confirm a size.
   * Sold-out and unavailable states take priority.
   *
   * @returns {boolean}
   */
  #isAwaitingSizeConfirmation() {
    const sizeGroups = this.#getSizeOptionGroups();

    this.#requiredSizeOptions = sizeGroups.length;

    if (this.dataset.variantAvailable !== 'true') {
      return false;
    }

    if (this.refs.addToCartButton.disabled) {
      return false;
    }

    return (
      this.#requiredSizeOptions > 0 &&
      this.#confirmedSizeOptions.size <
        this.#requiredSizeOptions
    );
  }

  /**
   * Scrolls to the next size selector that has not been confirmed.
   */
  #scrollToNextSizeOption() {
    const sizeGroups = this.#getSizeOptionGroups();

    const targetGroup =
      sizeGroups.find(
        (group) =>
          !this.#confirmedSizeOptions.has(
            this.#getSizeOptionKey(group)
          )
      ) || sizeGroups[0];

    if (!targetGroup) return;

    targetGroup.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    targetGroup.animate(
      [
        {
          outline: '2px solid transparent',
          outlineOffset: '4px',
        },
        {
          outline: '2px solid currentColor',
          outlineOffset: '4px',
        },
        {
          outline: '2px solid transparent',
          outlineOffset: '4px',
        },
      ],
      {
        duration: 1200,
        easing: 'ease-out',
      }
    );
  }

  /**
   * Gets the initial quantity.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(this.dataset.initialQuantity || '1') || 1;

    this.#updateButtonText();
  }

  /**
   * Updates the sticky button text and quantity.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    const buttonLabel =
      addToCartButton.querySelector(
        '.add-to-cart-text__content > span:first-child'
      );

    const available =
      this.dataset.variantAvailable === 'true' &&
      !addToCartButton.disabled;

    const awaitingSize =
      available &&
      this.#isAwaitingSizeConfirmation();

    this.dataset.awaitingSize =
      awaitingSize ? 'true' : 'false';

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    // Preserve Shopify's Sold Out or Unavailable wording.
    if (!available) {
      quantityDisplay.style.display = 'none';
      return;
    }

    if (buttonLabel) {
      if (awaitingSize) {
        buttonLabel.textContent =
          this.#requiredSizeOptions > 1
            ? 'Select sizes'
            : 'Select size';
      } else {
        buttonLabel.textContent = 'Add to cart';
      }
    }

    if (
      !awaitingSize &&
      this.#currentQuantity > 1
    ) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}